import { useQuery } from '@tanstack/react-query';
import { placesApi } from '@/features/places/api/placesApi';
import { Place } from '@/types/place';

/**
 * Filter object type matching backend API expectations.
 * Arrays should be comma-separated strings for API calls.
 */
export interface PlaceFilters {
  type?: string;
  age_category?: string; // Comma-separated string
  tags?: string; // Comma-separated string
  kid_friendly?: boolean;
  disability_friendly?: boolean;
  country?: string;
  rating?: number;
}

/**
 * Hook for fetching places data with TanStack Query.
 * Accepts filters object and returns places array with loading/error states.
 */
export const usePlacesData = (filters: PlaceFilters = {}) => {
  const {
    data,
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: ['places', filters],
    queryFn: () => placesApi.getAll(filters),
    staleTime: 60000, // 60 seconds (matches Phase 2 config)
  });

  // Extract places array from API response
  // Backend returns Place[] directly, placesApi.getAll() already extracts res.data
  // So data is already the Place[] array
  const places: Place[] = Array.isArray(data) ? data : [];

  return {
    places,
    isLoading,
    error: error as Error | null,
    refetch,
  };
};
